import { CommonModule, DecimalPipe } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TrimNumber } from 'src/shared/pipes/number/trim-number.pipe';
import { MarketService } from 'src/shared/services/market/market.service';
import { NewsCardComponent } from '@shared/components/news-card/news-card.component';
import { MiniLineChartComponent } from '@shared/components/mini-line-chart/mini-line-chart.component';
import { StockChartPanelComponent } from '@shared/components/stock-chart-panel/stock-chart-panel.component';
import { AnalyticsService } from '@core/services/analytics/analytics.service';
import {
  NORMALIZED_KEY_SECTOR_COUNT,
  NORMALIZED_KEY_STOCK_COUNT,
} from 'src/shared/constants/normalized-keys.constants';
import {
  HeatmapSectorData,
  MarketStatistics,
  ProfitLossData,
  Sector,
  StockData,
  StockPriceChart,
} from '../company/company.model';
import {
  MarketSummaryChartResponse,
  PortfolioVsMarketComparisonResponse,
  StockPriceMarketMinimalResponse,
} from 'src/shared/models/chart.model';
import { DATE_FILTER_PERIODS, DateFilterPeriod } from '@shared/constants/date-filter.constants';
import { resolveDateFilterRange } from '@shared/utils/date-filter.util';
import { ChartService } from 'src/shared/services/chart/chart.service';
import { SectorsService } from 'src/shared/services/sectors/sectors.service';
import { withLoading } from 'src/shared/operators/loading.operator';
import { forkJoin, interval } from 'rxjs';
import { POLLING_INTERVALS } from '@shared/constants/polling.constants';
import { SkeletonModule } from 'primeng/skeleton';
import { ProgressBarModule } from 'primeng/progressbar';
// For dynamic progressbar demo
import { ToastModule } from 'primeng/toast';
import { AvatarModule } from 'primeng/avatar';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TabsModule } from 'primeng/tabs';
import { HeatmapChartComponent } from '@shared/components/heatmap-chart/heatmap-chart.component';
import { InfoTooltipComponent } from '@shared/components/info-tooltip/info-tooltip.component';
import { ProfitLossTabsComponent } from '@shared/components/profit-loss-tabs/profit-loss-tabs.component';
import { EventCardComponent } from '@shared/components/event-card/event-card.component';
import { EventApiResponse, EventsService } from 'src/shared/services/events.service';
import { NewsService } from 'src/shared/services/news/news.service';
import { News } from 'src/shared/models/news.model';
import { LanguageService } from '@core/services/language/language.service';
import { CompanyService } from 'src/shared/services/company/company.service';
import { PermissionService } from 'src/shared/services/permission/permission.service';
import { PERMISSIONS } from 'src/shared/constants/permissions.constants';
import { PlatformService } from '@core/services/platform/platform.service';
import { ThemeService } from '@core/services/theme/theme.service';
@Component({
  selector: 'app-home',
  providers: [TrimNumber, DecimalPipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    NewsCardComponent,
    TranslateModule,
    RouterLink,
    FormsModule,
    MiniLineChartComponent,
    StockChartPanelComponent,
    SkeletonModule,
    ProgressBarModule,
    ToastModule,
    AvatarModule,
    ToggleSwitchModule,
    TabsModule,
    HeatmapChartComponent,
    InfoTooltipComponent,
    ProfitLossTabsComponent,
    EventCardComponent,
  ],
})
export class HomeComponent {
  NORMALIZED_KEY_SECTOR_COUNT = NORMALIZED_KEY_SECTOR_COUNT;
  NORMALIZED_KEY_STOCK_COUNT = NORMALIZED_KEY_STOCK_COUNT;
  isRtl = computed(() => this.languageService.isRTL());

  hasPortfolioVsMarketComparisonPermission = computed(() =>
    this.permissionService.hasPermission(PERMISSIONS.CHARTS_PORTFOLIO_VS_MARKET_COMPARISON)
  );

  isMobile = computed(() => this.platformService.isMobile() || window.innerWidth < 768);

  isMarketOpenToday = computed(() => this.marketService.isOpenToday());

  mobileRecordsCount = computed(() => (this.isMobile() ? 4 : 7));
  mobileEventsCount = computed(() => (this.isMobile() ? 1 : 4));

  news: News[] = [];
  marketStatistics: MarketStatistics = { properties: [] };
  sectors: Sector[] = [];
  withAramco: boolean = false;
  excludedStockSymbols: string[] = ['2222'];
  aramcoData = signal<any>(null);
  tasiMinimalData = signal<number[]>([]);
  tasiMinimalLastPrice = signal<number | null>(null);
  tasiMinimalLastPriceChange = signal<number | null>(null);
  tasiMinimalLastPriceChangePercent = signal<number | null>(null);
  isLoadingTasiMinimal = signal(true);

  tasiMinimalChartColor = computed<'red' | 'green'>(() => {
    const changePercent = this.tasiMinimalLastPriceChangePercent();
    if (changePercent === null) return 'green';
    return changePercent >= 0 ? 'green' : 'red';
  });

  sectorsCount = signal<string>('');
  companiesCount = signal<string>('');
  heatmapData = signal<HeatmapSectorData[]>([]);

  // Loading states using signals
  isLoadingNews = signal(true);
  isLoadingSectors = signal(true);
  isLoadingStatistics = signal(true);
  isLoadingStatisticsChart = signal(true);
  isLoadingHeatmapChart = signal(true);
  isLoadingUpcomingEvents = signal(true);
  stocksMarketCap: StockData[] = [];
  stocksTrailingPe: StockData[] = [];
  stocksEps: StockData[] = [];

  stocksProfitLoss = signal<ProfitLossData>({
    topGainers: [],
    topLosers: [],
    topVolume: [],
    topValue: [],
  });
  gradientRanges = signal<any[]>([]);
  upcomingEvents = signal<EventApiResponse[]>([]);

  // Comparison card: Portfolio vs TASI (from portfolioVsMarketComparison)
  portfolioVsMarketComparison = signal<PortfolioVsMarketComparisonResponse | null>(null);
  isLoadingPortfolioVsMarketComparison = signal(false);

  // TASI full chart (stock-chart-panel)
  isLoadingMarketChart = signal(false);
  marketChartData = signal<StockPriceChart[]>([]);
  marketChartResponse = signal<MarketSummaryChartResponse | null>(null);
  selectedMarketPeriod = signal<DateFilterPeriod>(
    (sessionStorage.getItem('market-chart-period') as DateFilterPeriod | null) ??
      DATE_FILTER_PERIODS.ONE_YEAR
  );
  private _marketFromDate = '';
  private _marketChartPeriod: string | null = null;

  comparisonDisplayData = computed(() => {
    const comp = this.portfolioVsMarketComparison();
    if (!comp?.data) return null;
    const d = comp.data;
    return {
      portfolioPercent: d.portfolioPerformance,
      tasiPercent: d.marketPerformance,
      difference: d.difference,
    };
  });

  comparisonNote = computed<{ key: string; params?: Record<string, string> }>(() => {
    const comp = this.portfolioVsMarketComparison();
    if (!comp) {
      return { key: 'home.portfolio_vs_tasi.portfolio_empty' };
    }
    if (comp.date === null) {
      return { key: 'home.portfolio_vs_tasi.portfolio_empty' };
    }
    const d = comp.data;
    if (d.exceededBy != null && d.exceededBy > 0) {
      return {
        key: 'home.portfolio_vs_tasi.outperforming',
        params: { percent: d.exceededBy.toFixed(1) },
      };
    }
    if (d.behindBy != null && d.behindBy > 0) {
      return {
        key: 'home.portfolio_vs_tasi.underperforming',
        params: { percent: d.behindBy.toFixed(1) },
      };
    }
    return { key: 'home.portfolio_vs_tasi.matches' };
  });

  private destroyRef = inject(DestroyRef);
  private analyticsService = inject(AnalyticsService);

  constructor(
    private marketService: MarketService,
    private router: Router,
    private chartService: ChartService,
    private sectorsService: SectorsService,
    private eventsService: EventsService,
    private newsService: NewsService,
    private languageService: LanguageService,
    private companyService: CompanyService,
    private permissionService: PermissionService,
    private platformService: PlatformService,
    private themeService: ThemeService
  ) {}

  ngOnInit() {
    this.marketService.startMarketStatePolling(this.destroyRef);

    this.updateMarketDateRange(this.selectedMarketPeriod());
    this.loadMarketChartData(true);
    interval(POLLING_INTERVALS.CHART_DATA)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadMarketChartData(false));

    // Load company news with loading state using custom operator

    this.newsService
      .getGlobalNews()
      .pipe(withLoading(this.isLoadingNews))
      .subscribe({
        next: (response) => {
          this.news = response.items;
        },
        error: (error) => {
          console.error(error);
        },
      });

    this.chartService
      .stockPriceMarketMinimal()
      .pipe(withLoading(this.isLoadingTasiMinimal))
      .subscribe({
        next: (res: StockPriceMarketMinimalResponse) => {
          this.tasiMinimalData.set(res.data.map((item) => item.close));
          this.tasiMinimalLastPrice.set(res.lastPrice);
          this.tasiMinimalLastPriceChange.set(res.lastPriceChange);
          this.tasiMinimalLastPriceChangePercent.set(res.lastPriceChangePercent);
        },
        error: (error) => {
          console.error(error);
        },
      });

    if (this.hasPortfolioVsMarketComparisonPermission()) {
      this.chartService
        .portfolioVsMarketComparison()
        .pipe(withLoading(this.isLoadingPortfolioVsMarketComparison))
        .subscribe((res) => {
          this.portfolioVsMarketComparison.set(res);
        });
    }

    // Load sectors with loading state using custom operator
    this.sectorsService
      .getSectorsList()
      .pipe(withLoading(this.isLoadingSectors))
      .subscribe({
        next: (res: Sector[]) => {
          this.sectors = res;
        },
        error: (error) => {
          console.error(error);
        },
      });
    this.marketService
      .getStatistics()
      .pipe(withLoading(this.isLoadingStatistics))
      .subscribe({
        next: (res: MarketStatistics) => {
          this.marketStatistics = res;
          for (const property of res.properties) {
            if (property.normalizedKey === NORMALIZED_KEY_SECTOR_COUNT) {
              this.sectorsCount.set(property.value);
            }
            if (property.normalizedKey === NORMALIZED_KEY_STOCK_COUNT) {
              this.companiesCount.set(property.value);
            }
          }
        },
        error: (error) => {
          console.error(error);
        },
      });

    forkJoin({
      marketCap: this.chartService.stocksMarketCap(
        this.excludedStockSymbols,
        this.mobileRecordsCount()
      ),
      trailingPe: this.chartService.stocksTrailingPe(this.mobileRecordsCount()),
      eps: this.chartService.stocksEps(this.mobileRecordsCount()),
    })
      .pipe(withLoading(this.isLoadingStatisticsChart))
      .subscribe({
        next: (res) => {
          this.stocksMarketCap = res.marketCap.data;
          this.stocksTrailingPe = res.trailingPe.data;
          this.stocksEps = res.eps.data;
        },
        error: (error) => {
          console.error(error);
        },
      });

    this.chartService
      .heatMap()
      .pipe(withLoading(this.isLoadingHeatmapChart))
      .subscribe((res) => {
        this.heatmapData.set(res.data);
        this.gradientRanges.set(res.gradientRanges);
      });

    this.chartService.stocksProfitLoss(this.mobileRecordsCount()).subscribe((res) => {
      this.stocksProfitLoss.set(res.data);
    });

    this.eventsService
      .getEvents(this.mobileEventsCount())
      .pipe(withLoading(this.isLoadingUpcomingEvents))
      .subscribe((res) => {
        this.upcomingEvents.set(res);
      });

    this.getCompanyImage('2222');
  }

  onMarketFilterClick(period: DateFilterPeriod) {
    this.updateMarketDateRange(period);
    this.loadMarketChartData(false);
    this.analyticsService.trackChartPeriodChanged({
      chartType: 'market_index',
      period,
      screenName: 'market',
    });
  }

  private updateMarketDateRange(period: DateFilterPeriod) {
    const { fromDate, chartPeriod } = resolveDateFilterRange(period);
    this._marketFromDate = fromDate;
    this._marketChartPeriod = chartPeriod;
    this.selectedMarketPeriod.set(period);
    sessionStorage.setItem('market-chart-period', period);
  }

  private loadMarketChartData(showLoading: boolean) {
    const source$ = this.chartService.createStockPriceMarket(
      this._marketFromDate,
      undefined,
      this._marketChartPeriod ?? undefined
    );

    (showLoading ? source$.pipe(withLoading(this.isLoadingMarketChart)) : source$).subscribe({
      next: (response) => {
        this.marketChartResponse.set(response);
        this.marketChartData.set(response.data || []);
      },
      error: (error) => {
        console.error(error);
      },
    });
  }

  navigateToSector(sectorId: string) {
    this.analyticsService.trackSectorViewed({ sectorId, entryPoint: 'home' });
    this.router.navigate(['/sectors', sectorId]);
  }

  onMarketIndexExpand(): void {
    this.analyticsService.trackMarketIndexExpanded({ indexName: 'tasi' });
  }

  onNewsArticleClick(articleId: string): void {
    this.analyticsService.trackNewsArticleOpened({
      articleId,
      surface: 'home_market_card',
    });
  }

  getMobileSectorColor(changePercentage: number): string {
    const isDark = this.themeService.currentTheme() === 'dark';
    const neutral = isDark ? '#4A5060' : '#6B7280';
    if (changePercentage === 0) return neutral;
    const ranges = this.gradientRanges();

    const match = ranges.find((r) => changePercentage >= r.min && changePercentage <= r.max);
    return match ? this.colorNameToHex(match.color) : neutral;
  }

  private colorNameToHex(name: string): string {
    const isDark = this.themeService.currentTheme() === 'dark';
    const map: Record<string, string> = isDark
      ? {
          Gray: '#4A5060',
          'Light Green': '#267A42',
          Green: '#2A9E4A',
          'Light Red': '#933840',
          Red: '#B03A3D',
        }
      : {
          Gray: '#6B7280',
          'Light Green': '#16A34A',
          Green: '#15803D',
          'Light Red': '#DC2626',
          Red: '#B91C1C',
        };
    return map[name] ?? (isDark ? '#4A5060' : '#6B7280');
  }

  changeAramcoData() {
    this.excludedStockSymbols = this.withAramco ? [] : ['2222'];
    this.chartService
      .stocksMarketCap(this.excludedStockSymbols, this.mobileRecordsCount())
      .subscribe((res) => {
        this.stocksMarketCap = res.data;
      });
  }

  navigateToStock(stockId: string) {
    this.router.navigate(['/sectors/company', stockId], {
      queryParams: {
        tab: 'profile',
      },
    });
  }

  getCompanyImage(symbol: string) {
    this.companyService.getCompanyImage(symbol).subscribe((res) => {
      this.aramcoData.set(res);
    });
  }
}
